import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserModalData } from '../../models/modal-data.model';
import { IUser, UserRole, UserState } from '../../models/user.model';
import { ROLE_LABELS } from '../../constants/role-access';

@Component({
  selector: 'app-user-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './user-modal.component.html',
  styleUrls: ['./user-modal.component.scss'],
})
export class UserModalComponent implements OnInit {
  public userForm!: FormGroup;
  public isEditMode = false;
  public isSubmitting = false;
  public UserRole = UserRole;
  public UserState = UserState;
  public roleOptions = Object.values(UserRole).map(role => ({ value: role, label: ROLE_LABELS[role] }));

  constructor(
    private fb: FormBuilder,
    @Inject('MODAL_DATA') private data: UserModalData,
    @Inject('MODAL_CLOSE') private modalClose: (result: IUser | null) => void
  ) { }

  ngOnInit(): void {
    this.isEditMode = this.data?.mode === 'edit';
    this.initForm();

    if (this.isEditMode && this.data?.user) {
      this.patchFormValues(this.data.user);
    }
  }

  private initForm(): void {
    this.userForm = this.fb.group({
      employeeId: [null, [Validators.required, Validators.min(1)]],
      firstname: ['', [Validators.required, Validators.minLength(2)]],
      lastname: ['', [Validators.required, Validators.minLength(2)]],
      qrToken: [''],
      role: [UserRole.FERTIGUNGSMITARBEITER, Validators.required],
      state: [UserState.ACTIVE, Validators.required],
    });
  }

  private patchFormValues(user: IUser): void {
    this.userForm.patchValue({
      employeeId: user.employeeId,
      firstname: user.firstname,
      lastname: user.lastname,
      qrToken: user.qrToken,
      role: user.role,
      state: user.state,
    });
  }

  public get title(): string {
    return this.isEditMode ? 'Benutzer bearbeiten' : 'Benutzer hinzufügen';
  }

  public close(): void {
    this.modalClose(null);
  }

  public submit(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const formValue = this.userForm.value;

    const user: IUser = {
      ...(this.isEditMode && this.data?.user?._id ? { _id: this.data.user._id } : {}),
      employeeId: formValue.employeeId,
      firstname: formValue.firstname,
      lastname: formValue.lastname,
      qrToken: formValue.qrToken || '',
      role: formValue.role,
      state: formValue.state,
    };

    this.modalClose(user);
  }

  public isFieldInvalid(fieldName: string): boolean {
    const field = this.userForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  @HostListener('document:keydown.escape', ['$event'])
  public handleEscape(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    this.modalClose(null);
  }

  public getFieldError(fieldName: string): string {
    const field = this.userForm.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) return 'Dieses Feld ist erforderlich';
    if (field.errors['minlength']) return `Mindestens ${field.errors['minlength'].requiredLength} Zeichen`;
    if (field.errors['min']) return 'Wert muss größer als 0 sein';

    return '';
  }
}
